import type { VacationRange, Zone } from '../types';

interface ApiRecord {
  description: string;
  population: string;
  start_date: string;
  end_date: string;
  location: string;
  zones: string;
  annee_scolaire: string;
}

interface ApiResponse {
  total_count: number;
  results: ApiRecord[];
}

interface VacationRangeJSON {
  start: string;
  end: string;
  zones: Zone[];
  description: string;
}

const PARIS_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Paris',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function toParisDate(iso: string): string {
  const d = new Date(iso);
  return PARIS_FMT.format(d);
}

function parseZone(zoneStr: string): Zone | null {
  // The dataset stores a single zone per record (e.g. "Zone A").
  // We don't accept overseas / Corse here — they have their own labels.
  if (/Zone\s*A\b/i.test(zoneStr)) return 'A';
  if (/Zone\s*B\b/i.test(zoneStr)) return 'B';
  if (/Zone\s*C\b/i.test(zoneStr)) return 'C';
  return null;
}

function endInclusive(isoExclusive: string): string {
  // The API end_date is the midnight after the last vacation day.
  const d = new Date(isoExclusive);
  d.setUTCDate(d.getUTCDate() - 1);
  return toParisDate(d.toISOString());
}

function isVacationPeriod(description: string): boolean {
  // Keep formal vacation periods only; drop "Pont de l'Ascension", "Début des Vacances", etc.
  return /^Vacances\s/.test(description);
}

function isStudentRow(population: string): boolean {
  // The dataset uses "Élèves" for student-only rows (notably summer), "Enseignants" for
  // teachers, and "-" for shared rows. Keep students and shared, drop teachers.
  return population === 'Élèves' || population === '-' || population === '';
}

function groupRecords(records: ApiRecord[]): VacationRange[] {
  const grouped = new Map<string, VacationRange>();
  for (const rec of records) {
    if (!isVacationPeriod(rec.description)) continue;
    if (!isStudentRow(rec.population)) continue;
    const zone = parseZone(rec.zones);
    if (!zone) continue;
    const start = toParisDate(rec.start_date);
    const end = endInclusive(rec.end_date);
    const key = `${rec.description}|${start}|${end}`;
    let entry = grouped.get(key);
    if (!entry) {
      entry = { start, end, zones: new Set<Zone>(), description: rec.description };
      grouped.set(key, entry);
    }
    entry.zones.add(zone);
  }
  return Array.from(grouped.values()).sort((a, b) => a.start.localeCompare(b.start));
}

export function toJSON(ranges: VacationRange[]): VacationRangeJSON[] {
  return ranges.map((r) => ({ ...r, zones: Array.from(r.zones).sort() as Zone[] }));
}

export function fromJSON(data: VacationRangeJSON[]): VacationRange[] {
  return data.map((r) => ({ ...r, zones: new Set(r.zones) }));
}

export async function fetchSchoolYear(schoolYear: string, signal?: AbortSignal): Promise<VacationRange[]> {
  const where = encodeURIComponent(`annee_scolaire = "${schoolYear}"`);
  const url = `https://data.education.gouv.fr/api/explore/v2.1/catalog/datasets/fr-en-calendrier-scolaire/records?where=${where}&limit=100`;
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`vacation API ${res.status}`);
  const data = (await res.json()) as ApiResponse;
  let all: ApiRecord[] = data.results;
  // Paginate if needed.
  for (let offset = 100; offset < data.total_count; offset += 100) {
    const r2 = await fetch(`${url}&offset=${offset}`, { signal });
    if (!r2.ok) break;
    const d2 = (await r2.json()) as ApiResponse;
    all = all.concat(d2.results);
  }
  return groupRecords(all);
}

export function schoolYearsForCalendarYear(year: number): [string, string] {
  return [`${year - 1}-${year}`, `${year}-${year + 1}`];
}
